import {view, Divider, tokens, Icon} from '@sail/ui';
import {refund as refundIcon} from '@sail/icons/react/Icon';
import {useIntl as useSailIntl} from '@sail/intl';
import {defineMessages, useIntl} from 'react-intl';
import {useHover} from '@sail/react-aria';
import {AgentCallResponse} from '@stripe-internal/conversations-client';
import {useWidget} from '../../../Widget/useWidget';
import {StatusBadge} from './StatusBadges/StatusBadge';

type RefundCardProps = {
  agentCallResponse: AgentCallResponse;
  onClick?: () => void;
};

const messages = defineMessages({
  cardTitle: {
    id: 'conversations_ui.refund_card.title',
    description: 'Refund Card title',
    defaultMessage: 'Refund',
  },
});

export const RefundCard = ({agentCallResponse, onClick}: RefundCardProps) => {
  const intl = useIntl();
  const sailIntl = useSailIntl();
  const widget = useWidget();
  const {hoverProps, isHovered} = useHover({});

  const {refund, customer, charge} = agentCallResponse;
  if (!refund) {
    return null;
  }

  const {amount, currency} = refund || {};
  const {name: customerName, email: customerEmail} = customer || {};
  const {id: chargeId} = charge || {};

  const displayName = () => {
    if (customerName) {
      return customerName;
    }
    if (customerEmail) {
      return customerEmail;
    }
  };

  return (
    <view.div
      data-testid="refund-card"
      css={{
        backgroundColor: 'surface',
        display: 'flex',
        alignItems: 'center',
        borderRadius: 'medium',
        paddingTop: 'small',
        paddingBottom: 'small',
        paddingLeft: 'space.150',
        paddingRight: 'space.150',
        borderColor: 'border',
        borderWidth: 'small',
        borderStyle: 'solid',
        maxWidth: '400px',
        position: 'relative',
        transition: 'box-shadow 0.5s',
        ':hover': {
          cursor: 'pointer',
          boxShadow: tokens.shadow.large.toString(),
        },
      }}
      {...hoverProps}
      onClick={() => {
        widget.requestAction({
          name: 'navigate',
          data: {
            href: `/payments/${chargeId}`,
          },
        });
        onClick?.();
      }}
    >
      <view.div
        css={{
          stack: 'y',
          gap: 'small',
          width: 'fill',
          minWidth: 'space.0',
        }}
      >
        <view.div
          css={{
            stack: 'x',
            gap: tokens.space[75].toString(),
            alignItems: 'center',
          }}
        >
          <Icon
            icon={refundIcon}
            size="xsmall"
            css={{color: isHovered ? 'action.primary' : 'subdued'}}
          />
          <view.span
            css={{
              color: 'subdued',
              font: 'label.small.emphasized',
            }}
          >
            {intl.formatMessage(messages.cardTitle)}
          </view.span>
          {displayName() ? (
            <view.div css={{height: 'space.125', stack: 'x', width: 'space.0'}}>
              <Divider />
            </view.div>
          ) : null}
          {displayName() ? (
            <view.span
              css={{
                font: 'label.small',
                color: 'action.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                ':hover': {
                  color: 'action.primary.hovered',
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                widget.requestAction({
                  name: 'navigate',
                  data: {
                    href: `/customers/${customer?.id}`,
                    route: {
                      name: 'CUSTOMERS_DETAIL',
                      params: {id: customer?.id},
                    },
                  },
                });
              }}
            >
              {displayName()}
            </view.span>
          ) : null}
        </view.div>
        <view.div
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: 'small',
            width: 'fill',
            minWidth: 'space.0',
          }}
        >
          {amount && currency ? (
            <view.span
              css={{
                font: 'heading.large',
                flexShrink: 0,
              }}
            >
              {sailIntl.formatCurrency(amount, {currency})}
            </view.span>
          ) : null}
          <view.div
            css={{
              minWidth: 'space.0',
              flex: 1,
              marginTop: 'xxsmall',
            }}
          >
            <StatusBadge refund={refund} />
          </view.div>
        </view.div>
      </view.div>
    </view.div>
  );
};
